import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, professionalsApi } from "../../../services";
import { Appointment, CompletedByEffortSummary, ProgressStep, Professional } from "../../../types";
import {
  formatDateForDisplay,
  formatTime,
  getBrazilCalendarMonthBounds,
  getTodayMonthYearBrazil,
} from "../../../utils/dateUtils";
import {
  formatProgressDuration,
  getProgressStepDurationMs,
  getProgressStepStatus,
  sortAppointmentsForEmployeeBoard,
} from "../../../utils/appointmentProgressUtils";
import { Loading, ErrorState, ActionButton, Avatar, Select } from "../../shared";
import { Icons } from "../../shared/Icons";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { useCompany } from "../../../contexts/CompanyContext";
import { EmployeeCompletedByEffortSummary } from "./EmployeeCompletedByEffortSummary";
import { CarsThroughputFiltersCard } from "../AdminDashboard/CarsThroughputFiltersCard";

function formatStepClock(iso: string | null): string {
  if (!iso) return "—";
  return formatTime(iso);
}

function sortedSteps(steps: ProgressStep[] | undefined): ProgressStep[] {
  if (!steps?.length) return [];
  return [...steps].sort((a, b) => a.id - b.id);
}

const stepStatusBadge: Record<string, string> = {
  completed: "bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-800",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800",
  pending: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600",
};

const stepStatusLabel: Record<string, string> = {
  completed: "Concluída",
  in_progress: "Em andamento",
  pending: "Pendente",
};

export const EmployeeProgressBoard: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();

  const [monthYear, setMonthYear] = useState(() => getTodayMonthYearBrazil());
  const monthBounds = useMemo(
    () => getBrazilCalendarMonthBounds(monthYear.year, monthYear.month),
    [monthYear.month, monthYear.year],
  );
  const startDate = monthBounds.startDate;
  const endDate = monthBounds.endDate;

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(true);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<number | null>(null);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  const [effortSummary, setEffortSummary] = useState<CompletedByEffortSummary | null>(null);
  const [effortLoading, setEffortLoading] = useState(false);
  const [effortError, setEffortError] = useState<string | null>(null);

  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedSubServiceId, setSelectedSubServiceId] = useState<number | null>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const loadProfessionals = async () => {
      if (!company?.id) return;
      setLoadingProfessionals(true);
      try {
        const response = await professionalsApi.getProfessionalsByCompany();
        if (response.success && response.data) {
          const active = response.data.filter((p) => p.isActive !== false);
          setProfessionals(active);
          setSelectedProfessionalId((prev) => {
            if (prev != null && active.some((p) => p.id === prev)) return prev;
            return active[0]?.id ?? null;
          });
        } else {
          setProfessionals([]);
        }
      } catch (e) {
        console.error(e);
        setProfessionals([]);
      } finally {
        setLoadingProfessionals(false);
      }
    };
    loadProfessionals();
  }, [company?.id]);

  useEffect(() => {
    if (!company || company.category !== "car" || selectedProfessionalId == null) {
      setEffortSummary(null);
      setEffortError(null);
      setEffortLoading(false);
      return;
    }

    let rangeStart = startDate;
    let rangeEnd = endDate;
    if (rangeStart > rangeEnd) {
      [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
    }

    let cancelled = false;

    (async () => {
      setEffortLoading(true);
      setEffortError(null);
      try {
        const res = await adminApi.getCompletedByEffort({
          startDate: rangeStart,
          endDate: rangeEnd,
          professionalId: selectedProfessionalId,
        });
        if (cancelled) return;
        if (res.success && res.data) {
          setEffortSummary(res.data);
        } else {
          setEffortSummary(null);
          setEffortError(res.error || "Erro ao carregar resumo por esforço");
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setEffortSummary(null);
          setEffortError("Erro ao carregar resumo por esforço");
        }
      } finally {
        if (!cancelled) setEffortLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company, selectedProfessionalId, startDate, endDate]);

  useEffect(() => {
    const load = async () => {
      if (!company || selectedProfessionalId == null) {
        setAppointments([]);
        return;
      }

      let rangeStart = startDate;
      let rangeEnd = endDate;
      if (rangeStart > rangeEnd) {
        [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
      }

      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getDashboardProgressSteps({
          professionalId: selectedProfessionalId,
          startDate: rangeStart,
          endDate: rangeEnd,
        });
        if (response.success && response.data) {
          setAppointments(sortAppointmentsForEmployeeBoard(response.data));
        } else {
          setError(response.error || "Erro ao carregar etapas de progresso");
          setAppointments([]);
        }
      } catch (e) {
        console.error(e);
        setError("Erro ao carregar etapas de progresso");
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [company, selectedProfessionalId, startDate, endDate]);

  useEffect(() => {
    setSelectedServiceId(null);
    setSelectedSubServiceId(null);
  }, [selectedProfessionalId, startDate, endDate]);

  const serviceOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const apt of appointments) {
      const name = apt.service?.name;
      if (apt.serviceId && name) {
        map.set(apt.serviceId, name);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [appointments]);

  const subServiceOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const apt of appointments) {
      if (selectedServiceId != null && apt.serviceId !== selectedServiceId) continue;
      for (const step of apt.progressSteps ?? []) {
        const sub = step.serviceSubService?.subService;
        if (sub?.id && sub.name) {
          map.set(sub.id, sub.name);
        }
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [appointments, selectedServiceId]);

  useEffect(() => {
    if (selectedServiceId != null && !serviceOptions.some((o) => o.value === selectedServiceId)) {
      setSelectedServiceId(null);
    }
  }, [serviceOptions, selectedServiceId]);

  useEffect(() => {
    if (
      selectedSubServiceId != null &&
      !subServiceOptions.some((o) => o.value === selectedSubServiceId)
    ) {
      setSelectedSubServiceId(null);
    }
  }, [subServiceOptions, selectedSubServiceId]);

  const sortedList = useMemo(() => {
    let list = appointments;

    if (selectedServiceId != null) {
      list = list.filter((apt) => apt.serviceId === selectedServiceId);
    }

    if (selectedSubServiceId != null) {
      list = list
        .map((apt) => ({
          ...apt,
          progressSteps: (apt.progressSteps ?? []).filter(
            (step) => step.serviceSubService?.subService?.id === selectedSubServiceId,
          ),
        }))
        .filter((apt) => (apt.progressSteps?.length ?? 0) > 0);
    }

    return sortAppointmentsForEmployeeBoard(list);
  }, [appointments, selectedServiceId, selectedSubServiceId]);

  const showServiceSubServiceFilters =
    appointments.length > 0 && (serviceOptions.length > 0 || subServiceOptions.length > 0);

  if (!company) {
    return null;
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <CarsThroughputFiltersCard
        month={monthYear.month}
        year={monthYear.year}
        onMonthYearChange={(month, year) => setMonthYear({ month, year })}
        disabled={loading || loadingProfessionals}
        professionals={professionals}
        selectedProfessionalId={selectedProfessionalId}
        onSelectProfessional={setSelectedProfessionalId}
        loadingProfessionals={loadingProfessionals}
        hideAllOption
      />

      {company.category === "car" && selectedProfessionalId != null && (
        <EmployeeCompletedByEffortSummary
          summary={effortSummary}
          loading={effortLoading}
          error={effortError}
          rangeStartDate={startDate <= endDate ? startDate : endDate}
          rangeEndDate={startDate <= endDate ? endDate : startDate}
        />
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
        <h3 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white">Agendamentos e etapas</h3>

        {showServiceSubServiceFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pb-1 border-b border-gray-100 dark:border-slate-700">
            {serviceOptions.length > 0 && (
              <Select
                label="Serviço"
                value={selectedServiceId ?? ""}
                onChange={(value) => {
                  setSelectedServiceId(value === "" ? null : Number(value));
                  setSelectedSubServiceId(null);
                }}
                options={[
                  { value: "", label: "Todos os serviços" },
                  ...serviceOptions,
                ]}
                placeholder="Todos os serviços"
                disabled={loading}
                searchable={serviceOptions.length > 5}
              />
            )}
            {subServiceOptions.length > 0 && (
              <Select
                label="Subserviço"
                value={selectedSubServiceId ?? ""}
                onChange={(value) =>
                  setSelectedSubServiceId(value === "" ? null : Number(value))
                }
                options={[
                  { value: "", label: "Todos os subserviços" },
                  ...subServiceOptions,
                ]}
                placeholder="Todos os subserviços"
                disabled={loading}
                searchable={subServiceOptions.length > 5}
              />
            )}
          </div>
        )}

        {selectedProfessionalId == null ? (
          <p className="text-sm text-gray-600 dark:text-slate-300 text-center py-8">Selecione um profissional.</p>
        ) : loading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} />
        ) : sortedList.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-slate-300 text-center py-8">
            {appointments.length === 0
              ? "Nenhum agendamento com etapas no período."
              : "Nenhum agendamento corresponde aos filtros selecionados."}
          </p>
        ) : (
          <div className="space-y-4">
            {sortedList.map((apt) => (
              <div
                key={apt.id}
                className="border border-gray-200 dark:border-slate-600 rounded-xl p-3 sm:p-4 bg-white dark:bg-slate-900/50 hover:bg-gray-50 dark:hover:bg-slate-700/40 hover:border-gray-300 dark:hover:border-slate-500 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-semibold text-gray-900 dark:text-white">{apt.client?.name || "Cliente"}</span>
                      {apt.clientCar && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-slate-300">
                          <CarBrandLogo brand={apt.clientCar.brand} />
                          <span>
                            {apt.clientCar.model} · {apt.clientCar.plate}
                            {apt.clientCar.effort ? (
                              <span className="text-gray-500 dark:text-slate-400"> · {apt.clientCar.effort}</span>
                            ) : null}
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-slate-300 truncate">
                      {apt.service?.name || "Serviço"}
                      {apt.appointmentDate ? (
                        <span className="text-gray-500 dark:text-slate-400">
                          {" "}
                          · {formatDateForDisplay(apt.appointmentDate)}
                        </span>
                      ) : null}
                      {apt.startTime ? (
                        <span className="text-gray-500 dark:text-slate-400"> · {formatTime(apt.startTime)}</span>
                      ) : null}
                    </p>
                  </div>
                  <ActionButton
                    variant="primary"
                    size="sm"
                    icon={<Icons.Visibility sx={{ fontSize: 18 }} />}
                    onClick={() => navigate(`/admin/appointments/${apt.id}`)}
                    className="w-full sm:w-auto shrink-0"
                  >
                    Ver agendamento
                  </ActionButton>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-600 pt-3 mt-1">
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    Progresso por etapa
                  </h4>
                  {!apt.progressSteps?.length ? (
                    <p className="text-xs text-gray-500 dark:text-slate-400">Nenhuma etapa neste agendamento.</p>
                  ) : (
                    <ul className="space-y-2">
                      {sortedSteps(apt.progressSteps).map((step) => {
                        const status = getProgressStepStatus(step);
                        const durationMs = getProgressStepDurationMs(step, now);
                        const name = step.serviceSubService?.subService?.name || "Etapa";

                        return (
                          <li
                            key={step.id}
                            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${stepStatusBadge[status]}`}
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                {step.professionals && step.professionals.length > 0 && (
                                  <div
                                    className="flex items-center -space-x-2 shrink-0"
                                    title={step.professionals.map((p) => p.name).join(", ")}
                                  >
                                    {step.professionals.map((prof) => (
                                      <Avatar
                                        key={prof.id}
                                        text={prof.name}
                                        src={prof.picture}
                                        size="xs"
                                        variant="blue"
                                        className="ring-2 ring-white"
                                      />
                                    ))}
                                  </div>
                                )}
                                <span className="font-medium text-gray-900 dark:text-inherit truncate">{name}</span>
                                <span className="text-xs opacity-80 shrink-0">
                                  ({stepStatusLabel[status]})
                                </span>
                              </div>
                              {step.professionals && step.professionals.length > 0 && (
                                <p className="text-xs mt-0.5 opacity-90 truncate pl-0.5">
                                  {step.professionals.map((pr) => pr.name).join(", ")}
                                </p>
                              )}
                              {step.notes?.trim() && (
                                <p className="text-xs mt-1 text-gray-700 pl-0.5 whitespace-pre-wrap">
                                  {step.notes}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm shrink-0">
                              <span>
                                {formatStepClock(step.startTime)} → {formatStepClock(step.endTime)}
                              </span>
                              {status !== "pending" && (
                                <span
                                  className={`font-mono font-semibold ${
                                    status === "in_progress" ? "animate-pulse" : ""
                                  }`}
                                >
                                  {formatProgressDuration(durationMs)}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
